
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { SwapRequestData, SwapRequestResult } from "./types";
import { toast } from "sonner";
import { fetchReagentById } from "../reagentService";
import { sendSwapOfferNotification } from "../notifications";
import { verifyCreditBalance } from "./credit/creditOfferHandler";
import { handleExistingReagentOffer, handleNewReagentOffer } from "./reagent/reagentOfferHandler";
import { sendMessageToOwner } from "./messaging/messageService";

export const createSwapRequest = async (data: SwapRequestData): Promise<SwapRequestResult> => {
  console.log("Creating swap request with data:", data);
  
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData.session) {
      console.error("Authentication error:", sessionError);
      return { 
        success: false, 
        error: "You must be logged in to create a swap request" 
      };
    }
    
    const userId = sessionData.session.user.id;

    // Prevent duplicate swap requests: check if one already exists for this user and reagent
    const { data: existingRequest } = await supabase
      .from("swap_requests")
      .select("id, status")
      .eq("reagent_id", data.reagentId)
      .eq("requester_id", userId)
      .maybeSingle();

    if (existingRequest) {
      console.log("Duplicate swap request found, returning existing id:", existingRequest.id);
      return { success: true, id: existingRequest.id, alreadyExists: true };
    }
    
    // Check if user has enough credits when offering credits
    if (data.offerType === "credits" && data.creditAmount) {
      const hasSufficientCredits = await verifyCreditBalance(userId, data.creditAmount);
      if (!hasSufficientCredits) {
        return {
          success: false,
          error: "You don't have enough credits for this swap request"
        };
      }
    }
    
    const swapRequestId = uuidv4();
    
    const { data: swapRequest, error: swapRequestError } = await supabase
      .from("swap_requests")
      .insert({
        id: swapRequestId,
        reagent_id: data.reagentId,
        requester_id: userId,
        status: "pending",
        credit_offer: data.offerType === "credits" ? data.creditAmount : 0,
        offered_reagent_id: data.offerType === "reagent" ? data.offeredReagentId : null
      })
      .select()
      .single();
      
    if (swapRequestError) {
      console.error("Error creating swap request:", swapRequestError);
      // Handle duplicate constraint: return existing swap request id
      if ((swapRequestError as any).code === "23505") {
        const { data: existing } = await supabase
          .from("swap_requests")
          .select("id")
          .eq("reagent_id", data.reagentId)
          .eq("requester_id", userId)
          .maybeSingle();
        if (existing) {
          return { success: true, id: existing.id, alreadyExists: true };
        }
        return { success: false, error: "A swap request for this reagent already exists" };
      }
      return { 
        success: false, 
        error: swapRequestError.message || "Failed to create swap request" 
      };
    }
    
    // Handle reagent offers
    if (data.offerType === "reagent") {
      if (data.offeredReagentId) {
        await handleExistingReagentOffer(swapRequestId, data.offeredReagentId);
      } else if (data.offeredReagent) {
        await handleNewReagentOffer(swapRequestId, data.offeredReagent);
      }
    }
    
    // Send message to the reagent owner
    const success = await sendMessageToOwner(data.reagentId, userId, data.message, swapRequestId);
    
    // Send notification email if needed
    await sendNotificationIfNeeded(swapRequest, data.reagentId, userId);
    
    return { 
      success: true, 
      id: swapRequestId,
      ...(success ? {} : { error: "Swap request created but failed to notify the owner" })
    };
    
  } catch (error) {
    console.error("Exception in createSwapRequest:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unexpected error occurred" 
    };
  }
};

// Helper function to send notification email
async function sendNotificationIfNeeded(swapRequest: any, reagentId: string, userId: string): Promise<void> {
  try {
    // Fetch reagent details for notification
    const reagent = await fetchReagentById(reagentId);
    if (reagent && reagent.user_id) {
      // Create a partial SwapRequest object with the required properties for notification
      const swapRequestForNotification = {
        id: swapRequest.id,
        reagent_id: swapRequest.reagent_id,
        requester_id: swapRequest.requester_id
      };
      
      // Send notification email to reagent owner
      const emailSent = await sendSwapOfferNotification(swapRequestForNotification, reagent, reagent.user_id);
      if (emailSent) {
        console.log("Swap offer notification email sent successfully");
      }
    }
  } catch (error) {
    console.error("Failed to send swap offer notification email:", error);
  }
}
